import { useCallback, useRef, useState } from 'react';
import {
  AbsoluteCenter,
  Box,
  Button,
  Divider,
  Flex,
  HStack,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { QRCodeSVG } from 'qrcode.react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Copy, Printer } from 'lucide-react';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { generateReferralActivationInfoPrintContent } from '../helpers/generateReferralActivationInfoPrintContent';
import { testIds } from './ReferralActivationInfo.testIds';

function displayURL(url: string): string {
  // Remove the protocol part
  return url.replace(/(^\w+:|^)\/\//, '');
}

export function ReferralActivationInfo({
  activationInfo,
}: {
  activationInfo: RouterOutputs['courseReferral']['getActivationInfo'];
}) {
  const qrCodeRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const [isCopying, setIsCopying] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);

  const generateQRCodePNG = useCallback(async (): Promise<string> => {
    // Generate the SVG data for the QR code
    const svgElement = qrCodeRef.current?.querySelector('svg');
    let pngImage = '';

    if (svgElement) {
      const svgData = new XMLSerializer().serializeToString(svgElement);
      const canvas = document.createElement('canvas');
      const img = new Image();

      // Convert SVG to PNG
      pngImage = await new Promise<string>((resolve, reject) => {
        img.onload = () => {
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.drawImage(img, 0, 0);
            resolve(canvas.toDataURL('image/png'));
          } else {
            reject(new Error('Failed to get canvas context'));
          }
        };
        img.onerror = () => reject(new Error('Failed to load SVG as image'));
        img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
      });
    }

    return pngImage;
  }, []);

  const generateContent = useCallback(async (): Promise<string> => {
    const pngImage = await generateQRCodePNG();

    // Construct the HTML content with the text and the QR code image
    return `
      <div style="font-family: Arial, sans-serif; font-size: 16px; color: #171923;">
        <p><strong>Visit website:</strong></p>
        <ol>
          <li>Open the link in a browser: <a href="${activationInfo.app_url}/join" target="_blank" style="color: #563FD8;">${displayURL(activationInfo.app_url)}/join</a></li>
          <li>Enter the code: <code style="font-family: monospace; background-color: #EDF2F7; padding: 2px 4px; border-radius: 4px;">${activationInfo.activation_code}</code></li>
        </ol>
        <p><strong>Or scan the QR code using your camera app or QR code scanner on your device:</strong></p>
        ${pngImage ? `<div><img src="${pngImage}" alt="QR Code" /></div>` : ''}
      </div>
    `;
  }, [activationInfo.activation_code, activationInfo.app_url, generateQRCodePNG]);

  const handleCopyToClipboard = useCallback(async () => {
    if (isCopying) {
      return;
    }
    setIsCopying(true);

    try {
      const htmlContent = await generateContent();

      const clipboardItem = new ClipboardItem({
        'text/html': new Blob([htmlContent], { type: 'text/html' }),
      });

      await navigator.clipboard.write([clipboardItem]);

      toast({
        title: t`Copied to clipboard`,
        description: t`You can now paste it in an email and send it to your participant`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: t`Failed to copy`,
        description: t`Please try again`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCopying(false);
    }
  }, [generateContent, isCopying, toast]);

  const handlePrint = useCallback(async () => {
    if (isPrinting) {
      return;
    }
    setIsPrinting(true);

    try {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        const now = new Date();
        const formattedDate = now.toLocaleDateString();
        const formattedTime = now.toLocaleTimeString();
        const pngImage = await generateQRCodePNG();

        printWindow.document.open();
        printWindow.document.write(
          generateReferralActivationInfoPrintContent({
            activationCode: activationInfo.activation_code,
            appUrl: displayURL(activationInfo.app_url),
            courseTitle: activationInfo.course_title,
            formattedDate,
            formattedTime,
            pngImage,
          }),
        );
        printWindow.document.close();

        // Wait for the image to load before opening the print dialog
        printWindow.onload = () => {
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        };
      }
    } catch (error) {
      toast({
        title: t`Failed to print`,
        description: t`Please try again`,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsPrinting(false);
    }
  }, [generateContent, isPrinting, toast]);

  return (
    <VStack>
      {/* header */}
      <Flex w="full" px="24px" py="8px" justify="center" align="center">
        <Text
          fontSize="lg"
          lineHeight="7"
          color="fg.muted"
          fontWeight="semibold"
          textAlign="center"
        >
          <Trans>Share the following activation details with your participant.</Trans>
          <br />
          <Trans>This will allow your participant to enrol themselves in the agreed course.</Trans>
          <br />
          <Trans>This code can be used only once.</Trans>
        </Text>
      </Flex>

      {/* activation code */}
      <HStack w="full" px="40px" py="20px" justify="center" align="start" gap="20px">
        <VStack align="start" justify="start" w="full" gap="16px">
          <Text fontSize="xl" lineHeight="7" color="fg.default" fontWeight="semibold">
            <Trans>Visit website</Trans>
          </Text>
          <VStack align="start" justify="start" w="full" gap="8px">
            <Text fontSize="md" lineHeight="6" color="fg.emphasis">
              <Trans>1. Open the link in a browser</Trans>
            </Text>
            <Flex
              align="center"
              justify="center"
              alignSelf="stretch"
              w="full"
              p="16px"
              bg="bg.emphasis"
              borderRadius="8px"
            >
              <Text
                fontSize="2xl"
                lineHeight="8"
                color="fg.default"
                fontWeight="semibold"
                textAlign="center"
                data-testid={testIds.url}
              >
                {displayURL(activationInfo.app_url)}/join
              </Text>
            </Flex>
          </VStack>
          <VStack align="start" justify="start" w="full" gap="8px">
            <Text fontSize="md" lineHeight="6" color="fg.emphasis">
              <Trans>2. Enter the code</Trans>
            </Text>
            <Flex
              align="center"
              justify="center"
              alignSelf="stretch"
              w="full"
              p="16px"
              bg="bg.emphasis"
              borderRadius="8px"
            >
              <Text
                fontSize="3xl"
                lineHeight="10"
                color="fg.default"
                fontWeight="semibold"
                fontFamily="monospace"
                textAlign="center"
                data-testid={testIds.code}
              >
                {activationInfo.activation_code}
              </Text>
            </Flex>
          </VStack>
        </VStack>

        <Box position="relative" px="10px" height="full">
          <Divider orientation="vertical" height="280px" borderColor="border.active" />
          <AbsoluteCenter bg="bg.muted" px="4">
            <Text fontSize="xl" lineHeight="7" color="fg.muted" fontWeight="semibold">
              <Trans>or</Trans>
            </Text>
          </AbsoluteCenter>
        </Box>

        <VStack align="start" justify="start" w="full" gap="16px">
          <Text fontSize="xl" lineHeight="7" color="fg.default" fontWeight="semibold">
            <Trans>Scan QR code</Trans>
          </Text>
          <Text fontSize="md" lineHeight="6" color="fg.emphasis">
            <Trans>To scan, use your camera app or QR code scanner on your device</Trans>
          </Text>
          <Flex
            ref={qrCodeRef}
            w="200px"
            height="200px"
            bg="bg.surface"
            borderRadius="16px"
            align="center"
            justify="center"
            alignSelf="center"
            p="20px"
          >
            <QRCodeSVG
              value={`${activationInfo.app_url}/join?activation_code=${activationInfo.activation_code}`}
              size={160}
            />
          </Flex>
        </VStack>
      </HStack>

      {/* footer with action buttons */}
      <HStack w="full" align="center" justify="center" px="24px" py="16px" gap="10px">
        <Button
          leftIcon={<Copy size="20px" />}
          variant="outline"
          color="fg.emphasis"
          borderColor="border.emphasis"
          onClick={handleCopyToClipboard}
          isLoading={isCopying}
        >
          <Trans>Copy to clipboard</Trans>
        </Button>
        <Button
          leftIcon={<Printer size="20px" />}
          variant="outline"
          color="fg.emphasis"
          borderColor="border.emphasis"
          onClick={handlePrint}
          isLoading={isPrinting}
        >
          <Trans>Print</Trans>
        </Button>
      </HStack>
    </VStack>
  );
}
